import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CancelIcon from '@mui/icons-material/Cancel';
import Divider from "@mui/material/Divider";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from "@mui/material/IconButton";
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from '../helpers/Table'
import DeletePopup from "../helpers/DeletePopup";

import { addDriver, editDriver, deleteDriver, clearQueryResult } from "../../actions/valueActions";

import { formatString, convertBoolDiffValue } from "./valueFunctions";
// import firstCharToUpper from "../helpers/stringFormat";
import CostValueDriverOperatingForm from "./CostValueDriverOperatingForm";
import { getIconFromDriverName } from "./iconFunctions";
import ModalElement from "../Modal";
import AddOperatingCostData from "./AddOperatingCostData";
import { toolTipUserIcon } from "./iconFunctions";
import { getAllOutcomes } from "./valueFunctions";

const CostValueDriverCompliance = ({ id, valueModel, costs, driver, outcome }) => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();

    const [open, setOpen] = React.useState(false);
    const [selectedCost, setSelectedCost] = useState(Object.keys(costs)[0])
    const [deleteComplianceDriverPopupOpen, setDeleteComplianceDriverPopupOpen] = useState(false);
    const [deleteComplianceDriverId, setDeleteComplianceDriverId] = useState(null);
    const [editComplianceDriverId, setEditComplianceDriverId] = useState(null);
    const [addCostModalOpen, setAddCostModalOpen] = useState(false);

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleCostChange = (event) => {
        setSelectedCost(event.target.value)
        if (editComplianceDriverId) {
            setEditComplianceDriverId(null)
            if (formikRef.current) {
                formikRef.current.resetForm();
            }
        }
    }

    const openDeleteComplianceDriver = (id) => {
        setDeleteComplianceDriverId(id)
        setDeleteComplianceDriverPopupOpen(true)
    }

    const editComplianceDriver = (id) => {
        setEditComplianceDriverId(id)
        let complianceDriver = valueModel.cost_value_driver.compliance_savings.find(complianceDriver => complianceDriver.id === id)
        setSelectedCost(complianceDriver.sub_cost)
    }

    const toggleAddCostModalOpen = () => {
        setAddCostModalOpen(!addCostModalOpen)
        dispatch(clearQueryResult())
    }

    function renderAddCostModal() {
        return (
            <ModalElement
                modalOpen={addCostModalOpen}
                modalWidth={1000}
                handleClose={toggleAddCostModalOpen}
                header="Add Compliance Costs"
                instruction="Please specify what you would like to add and provide a wish that will bring some context to your request"
                content={
                    <AddOperatingCostData
                        id={id}
                        industry={valueModel?.industry}
                        type='compliance'
                        cost_category={driver}
                        costTypes={costs}
                        filterCosts={[]}
                        handleClose={toggleAddCostModalOpen}
                    />
                }
            />

        )
    }

    const onSubmitCompliance = async (values, driver, cost, { setSubmitting, setErrors }) => {
        values.id = editComplianceDriverId
        if (outcome) {
            values.outcome = outcome
        }
        values.cost = driver
        values.sub_cost = cost
        values.multiple_value = values.multiple_value.toString().replace(/,/g, "");
        let success = false

        if (editComplianceDriverId) {
            const result = await dispatch(editDriver('compliance', id, values, { setSubmitting, setErrors }))
            success = result?.success;
            if (success) {
                setEditComplianceDriverId(null)
            }
        }
        else {
            const result = await dispatch(addDriver('compliance', id, values, { setSubmitting, setErrors }))
            success = result?.success;
        }

        if (formikRef.current && success) {
            formikRef.current.resetForm();
        }
    }

    const handleResetCompliance = () => {
        setEditComplianceDriverId(null)

        // Access the formikRef current value and call resetForm
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    };

    const complianceDriverColumns = [
        { id: 'outcome', label: 'Outcome', minWidth: 170, action: false },
        { id: 'driver_name', label: 'Name', minWidth: 170, action: false },
        // { id: 'sub_cost', label: 'Cost', format: (value) => formatString(value), minWidth: 150, action: false },
        // { id: 'multiple_type', label: 'Type', format: (value) => firstCharToUpper(value), minWidth: 150, action: false },
        { id: 'efficiency_gain', label: 'Change', minWidth: 100, action: false },
        { id: 'share_of_cost', label: '% of cost', format: (value) => value + ' %', minWidth: 100, action: false },
        { id: 'positive_driver', label: 'Differentiation value', minWidth: 100, format: (value) => convertBoolDiffValue(Boolean(value)), action: false },
        { id: 'actions', label: 'Actions', minWidth: 100, action: false },
    ];

    const createComplianceDriverData = (id, outcome, driver_name, sub_cost, multiple_type, efficiency_gain, share_of_cost, positive_driver, actions) => {
        efficiency_gain = `${efficiency_gain} % ${positive_driver ? 'saving' : 'extra cost'}`
        return { id, outcome, driver_name, sub_cost, multiple_type, efficiency_gain, share_of_cost, positive_driver, actions };
    }

    const complianceDriverActions = (id) => {
        return (
            <React.Fragment>
                <IconButton size="small" onClick={() => editComplianceDriver(id)}>
                    <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => openDeleteComplianceDriver(id)}>
                    <DeleteIcon />
                </IconButton>
            </React.Fragment>
        )
    }

    const complianceDriverRows = valueModel?.cost_value_driver.compliance_savings.filter(driver => !outcome || driver.outcome === outcome).map((driver) => {
        return (
            createComplianceDriverData(driver.id, driver.outcome, driver.driver_name, driver.sub_cost, driver.multiple_type, driver.efficiency_gain, driver.share_of_cost, driver.positive_driver, complianceDriverActions(driver.id))
        )
    })

    const complianceDriverTable = () => {
        return (
            <React.Fragment>
                <Divider />
                <Table
                    columns={complianceDriverColumns}
                    rows={complianceDriverRows}
                    selectedId={editComplianceDriverId}
                />
            </React.Fragment>
        )
    }

    const complianceDriverFormActions = () => {
        return (
            <React.Fragment>
                {editComplianceDriverId ? null
                    : <Button variant="contained" startIcon={<Box component={getIconFromDriverName(driver)} />} size="small" type="submit">Add driver</Button>
                }
                {editComplianceDriverId ?
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit">Save</Button>
                        <Button variant="contained" startIcon={<CancelIcon />} size="small" onClick={handleResetCompliance}>Cancel</Button>
                    </Stack>
                    : null
                }
            </React.Fragment>
        )
    }

    return (
        <Box>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Stack gap={1} direction="row" sx={{ ml: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        Driver type:
                    </Typography>
                    <Typography variant="caption">
                        Operating Costs Savings
                    </Typography>
                </Stack>
                <Box sx={{ width: '30%' }} />
                <Button startIcon={<AutoFixHighIcon />} onClick={toggleAddCostModalOpen} sx={{ textTransform: 'none' }}>Add Costs</Button>
            </Box>

            <Stack direction="column" gap={1} sx={{ ml: 2, mt: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    1. Select Cost Type
                </Typography>
                <Box>
                    <Select
                        open={open}
                        onClose={handleClose}
                        onOpen={handleOpen}
                        value={selectedCost}
                        onChange={handleCostChange}
                        size='small'
                    >
                        {Object.keys(costs).map((cost) => (
                            <MenuItem key={cost} value={cost}>
                                {/* {formatString(cost)} */}
                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                    {formatString(cost)}
                                    {costs[cost]?.user_created && toolTipUserIcon()}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Stack>

            <CostValueDriverOperatingForm
                currency="€"
                saving
                outcome={outcome}
                outcomeList={getAllOutcomes(valueModel?.driver_analysis)}
                costStructure={costs[selectedCost]}
                editOperatingDriverId={editComplianceDriverId}
                editOperatingDriver={valueModel?.cost_value_driver.compliance_savings.find(complianceDriver => complianceDriver.id === editComplianceDriverId)}
                formActions={complianceDriverFormActions}
                onSubmit={(values, { setSubmitting, setErrors }) => onSubmitCompliance(values, driver, selectedCost, { setSubmitting, setErrors })}
                ref={formikRef}
            />
            {complianceDriverTable()}
            <DeletePopup open={deleteComplianceDriverPopupOpen} onClose={() => setDeleteComplianceDriverPopupOpen(false)} onDelete={() => dispatch(deleteDriver('compliance', id, deleteComplianceDriverId))} />
            {renderAddCostModal(selectedCost)}
        </Box>
    )
}

export default CostValueDriverCompliance